import { Link } from 'react-router-dom'
import { ArrowUpRight, Clock } from 'lucide-react'
import type { BlogPost } from '../../content/blog'
import { formatDate } from '../../content/blog'
import { useT, useLanguage } from '../../i18n/LanguageContext'

/**
 * Cztery warianty gradientu na okładkę zastępczą. Wybór jest deterministyczny
 * (hash slugu), więc artykuł zawsze wygląda tak samo, a sąsiadujące kafle na
 * liście różnią się od siebie. Ten sam chwyt co kolorowanie rezerwacji
 * w Schedulerze — kolor niesie tożsamość, nie znaczenie.
 */
const coverGradients = [
  'from-brand-600/30 via-brand-800/20 to-transparent',
  'from-brand-400/25 via-brand-700/20 to-transparent',
  'from-accent-500/20 via-brand-800/25 to-transparent',
  'from-brand-500/25 via-brand-900/30 to-transparent',
]

function gradientFor(slug: string): string {
  let hash = 0
  for (let i = 0; i < slug.length; i++) {
    hash = (hash * 31 + slug.charCodeAt(i)) >>> 0
  }
  return coverGradients[hash % coverGradients.length]
}

interface PostCardProps {
  post: BlogPost
  /** Kafel wyróżniony — układ poziomy, większa typografia, pełna szerokość. */
  featured?: boolean
}

export default function PostCard({ post, featured = false }: PostCardProps) {
  const t = useT()
  const { language } = useLanguage()

  const meta = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
      <time dateTime={post.date} className="font-mono">
        {formatDate(post.date, language)}
      </time>
      <span aria-hidden="true" className="text-zinc-700">·</span>
      <span className="inline-flex items-center gap-1.5">
        <Clock size={12} aria-hidden="true" />
        {post.readingMinutes} {t.blog.readingTime}
      </span>
    </div>
  )

  const cover = post.cover ? (
    <img
      src={post.cover}
      alt=""
      aria-hidden="true"
      className="h-full w-full object-cover"
      loading="lazy"
    />
  ) : (
    <div
      aria-hidden="true"
      className={`h-full w-full bg-gradient-to-br ${gradientFor(post.slug)} bg-grid`}
    />
  )

  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`group relative flex overflow-hidden rounded-2xl bg-white/[0.05] border border-white/[0.08] hover:border-white/[0.16] hover:bg-white/[0.08] transition-all ${
        featured ? 'flex-col lg:flex-row' : 'flex-col'
      }`}
    >
      <div
        className={`relative shrink-0 overflow-hidden ${
          featured ? 'h-56 lg:h-auto lg:w-[45%]' : 'h-40'
        }`}
      >
        {cover}
        {featured && (
          <span className="absolute top-4 left-4 px-2.5 py-1 rounded-md bg-[#050507]/70 backdrop-blur-sm font-mono text-[10px] uppercase tracking-[0.18em] text-accent-400">
            {t.blog.featured}
          </span>
        )}
      </div>

      <div className={`flex flex-col flex-1 ${featured ? 'p-8 lg:p-10' : 'p-7'}`}>
        {post.category && (
          <p className="font-mono text-brand-400 text-xs tracking-[0.2em] uppercase mb-3">
            {post.category}
          </p>
        )}

        <h3
          className={`font-display font-bold text-white tracking-tight mb-3 ${
            featured ? 'text-2xl sm:text-3xl' : 'text-xl'
          }`}
        >
          {post.title}
        </h3>

        {post.lead && (
          <p
            className={`text-zinc-400 leading-relaxed mb-6 ${
              featured ? 'text-base sm:text-lg' : 'text-sm'
            }`}
          >
            {post.lead}
          </p>
        )}

        <div className="mt-auto flex items-end justify-between gap-4">
          {meta}
          <ArrowUpRight
            size={18}
            aria-hidden="true"
            className="shrink-0 text-zinc-500 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
          />
        </div>
      </div>
    </Link>
  )
}
